/**
 * PostHog ecommerce events.
 *
 * The store only had autocapture ($pageview / $autocapture / $rageclick), which
 * meant conversion could not be measured: a /cart pageview was the furthest-down
 * signal available and revenue was invisible entirely. This adds the funnel:
 *
 *   product_viewed -> product_added_to_cart -> cart_viewed -> checkout_started
 *
 * NOT covered here: the purchase itself. Shopify's checkout runs on its own
 * domain where theme JS never executes, so `order_completed` has to come from a
 * Shopify web pixel (Settings > Customer events). `checkout_started` below fires
 * on the click, so treat it as intent, not as "reached checkout".
 *
 * Every capture is wrapped in a try/catch: analytics must never break the store.
 *
 * Reads its data from snippets/analytics-context.liquid rather than scraping the
 * DOM, so prices and IDs come from Liquid and are already in major units.
 */
(() => {
  const CONTEXT_ID = 'analytics-context';
  const CHECKOUT_SELECTOR = 'button[name="checkout"]';

  // Sources that publish cartUpdate as the result of *adding* an item, versus
  // quantity edits and removals from the cart page itself. Keep in sync with the
  // publish(PUB_SUB_EVENTS.cartUpdate) call sites.
  const ADD_SOURCES = new Set(['product-form', 'quick-add', 'booking-widget', 'shared-shopping-lists']);

  const context = (() => {
    try {
      return JSON.parse(document.getElementById(CONTEXT_ID)?.textContent || '{}');
    } catch (error) {
      return {};
    }
  })();

  const capture = (event, properties) => {
    try {
      window.posthog?.capture?.(event, { ...properties, currency: context.currency });
    } catch (error) {
      /* Never let analytics break the page. */
    }
  };

  /**
   * Shopify's /cart/add.js returns either a bare line item or, when `sections`
   * are requested, an object wrapping `items`. Normalise both shapes.
   */
  const firstLineItem = (cartData) => {
    if (!cartData) return null;
    if (Array.isArray(cartData.items)) return cartData.items[0] || null;
    return cartData.id ? cartData : null;
  };

  /**
   * Stamp the PostHog ids onto the cart as attributes.
   *
   * Shopify's checkout runs on its own domain, so a web pixel firing
   * `order_completed` there cannot read PostHog's cookie and would land the
   * purchase on a brand new anonymous person, breaking every funnel. Cart
   * attributes survive into the checkout and the order, so the pixel can read
   * them back and attribute revenue to the right person.
   *
   * Fire and forget, once per session, and never blocks the add itself.
   */
  const STAMP_FLAG = 'ph_cart_stamped';
  const stampCartAttributes = () => {
    try {
      if (sessionStorage.getItem(STAMP_FLAG)) return;
      if (window.posthog?.has_opted_out_capturing?.()) return;
      const distinctId = window.posthog?.get_distinct_id?.();
      if (!distinctId) return;
      sessionStorage.setItem(STAMP_FLAG, '1');
      const body = JSON.stringify({
        attributes: {
          ph_distinct_id: distinctId,
          ph_session_id: window.posthog?.get_session_id?.() || '',
        },
      });
      fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).catch(() => {
        sessionStorage.removeItem(STAMP_FLAG);
      });
    } catch (error) {
      /* Attribution is best effort; never break the cart. */
    }
  };

  const trackAddToCart = ({ source, cartData, productVariantId }) => {
    if (!ADD_SOURCES.has(source)) return;
    stampCartAttributes();

    const item = firstLineItem(cartData);
    const quantity = item?.quantity ?? 1;
    // Line item prices are in cents; context prices are already in major units.
    const unitPrice = item ? item.price / 100 : context.product?.price;

    capture('product_added_to_cart', {
      product_id: item?.product_id ?? context.product?.product_id,
      variant_id: item?.variant_id ?? (Number(productVariantId) || productVariantId),
      name: item?.product_title ?? context.product?.name,
      variant_name: item?.variant_title ?? null,
      handle: item?.handle ?? context.product?.handle,
      vendor: item?.vendor ?? context.product?.vendor,
      price: unitPrice,
      quantity,
      value: unitPrice != null ? Number((unitPrice * quantity).toFixed(2)) : undefined,
      source,
    });
  };

  // posthog.init() runs inside theme.liquid's own DOMContentLoaded listener.
  // This file is deferred, so it executes after that inline script has
  // registered, which means this listener runs after init has happened.
  const descriptionTruncated = () => {
    const wrapper = document.querySelector('[data-collapsible-description]');
    return Boolean(wrapper) && !wrapper.classList.contains('is-short');
  };

  const curatorNote = () => document.querySelector('[data-curator-note]');
  const curatorNoteProperties = (note) => ({
    product_id: context.product?.product_id,
    handle: context.product?.handle,
    note: note.dataset.curatorNote,
    sections: note.dataset.curatorNoteSections.trim().split(/\s+/).filter(Boolean),
  });

  // "Seen" means at least half the card stayed on screen for a second, so a
  // fast scroll past it does not count as a read.
  const SEEN_RATIO = 0.5;
  const SEEN_MS = 1000;
  const watchCuratorNote = (note) => {
    if (!('IntersectionObserver' in window)) return;
    let timer = null;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.intersectionRatio >= SEEN_RATIO) {
        timer ??= setTimeout(() => {
          observer.disconnect();
          capture('curator_note_viewed', curatorNoteProperties(note));
        }, SEEN_MS);
      } else {
        clearTimeout(timer);
        timer = null;
      }
    }, { threshold: [0, SEEN_RATIO] });
    observer.observe(note);
  };

  window.addEventListener('DOMContentLoaded', () => {
    const note = curatorNote();
    if (note) watchCuratorNote(note);
    if (context.product) {
      capture('product_viewed', {
        ...context.product,
        description_truncated: descriptionTruncated(),
        curator_note: note?.dataset.curatorNote ?? null,
      });
    }
    if (context.collection) capture('collection_viewed', context.collection);
    if (context.search?.query) capture('search_submitted', context.search);
    if (context.page_type === 'cart') {
      capture('cart_viewed', {
        item_count: context.cart_item_count,
        value: context.cart_total,
      });
    }
  });

  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => trackAddToCart(data || {}));
  }

  document.addEventListener('monti:description-toggle', ({ detail }) => {
    capture('product_description_toggled', {
      product_id: context.product?.product_id,
      handle: context.product?.handle,
      expanded: detail.expanded,
      trigger: detail.trigger,
    });
  });

  // <details> fires `toggle` without bubbling, so listen in the capture phase.
  document.addEventListener('toggle', (event) => {
    const note = event.target.closest?.('[data-curator-note]');
    if (!note || !event.target.matches('.monti-curator-note__more')) return;
    capture('curator_note_toggled', { ...curatorNoteProperties(note), expanded: event.target.open });
  }, true);

  document.addEventListener('click', (event) => {
    const link = event.target.closest('.monti-curator-note__guide');
    if (!link) return;
    capture('curator_note_guide_clicked', { ...curatorNoteProperties(link.closest('[data-curator-note]')), url: link.getAttribute('href') });
  });

  // Intent to check out. Deliberately does not touch the submit path, see the
  // checkout pending state in cart.js for the same rule.
  document.addEventListener('click', (event) => {
    const button = event.target.closest(CHECKOUT_SELECTOR);
    if (!button || button.disabled) return;
    capture('checkout_started', {
      item_count: context.cart_item_count,
      value: context.cart_total,
      from: context.page_type,
    });
  });
})();
